import React, { useState, useEffect, useMemo } from 'react';
import { FaUser, FaEdit, FaTrash, FaEye, FaPlus, FaUserCheck, FaUserCog, FaSync, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

const ROLE_COLORS = {
  user: '#4CAF50',
  admin: '#F44336',
  userManagement: '#FFC107',
  inactive: '#9E9E9E'
};

export default function UsersTable() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('nombreCompleto');
  const [sortDirection, setSortDirection] = useState('asc');
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      let errorMessage = 'Error al cargar los usuarios. Por favor, intente de nuevo.';
      if (err.response && err.response.status === 404) {
        errorMessage = 'No se encontró la ruta de usuarios. Verifique la configuración del servidor.';
      } else if (err.request) {
        errorMessage = 'No se pudo conectar con el servidor. Verifique su conexión a internet.';
      }
      setError(errorMessage);
      console.error('Error fetching users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchUsers();
      } catch (err) {
        setError('Error al eliminar el usuario. Por favor, intente de nuevo.');
        console.error('Error deleting user:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSort = (column) => {
    setSortDirection(sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const filteredAndSortedUsers = useMemo(() => {
    return users
      .filter(user =>
        (user.nombreCompleto.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.usuario.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
        (roleFilter === '' || user.role === roleFilter)
      )
      .sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
        if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
  }, [users, searchTerm, sortColumn, sortDirection, roleFilter]);

  const roleData = useMemo(() => {
    const roleCounts = users.reduce((acc, user) => {
      acc[user.role] = (acc[user.role] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(roleCounts).map(([role, count]) => ({ role, count }));
  }, [users]);

  const activityData = useMemo(() => {
    const activeCount = users.filter(user => user.role !== 'inactive').length;
    const inactiveCount = users.length - activeCount;
    return [
      { name: 'Activos', value: activeCount },
      { name: 'Inactivos', value: inactiveCount }
    ];
  }, [users]);

  const activeUsers = users.filter(user => user.role !== 'inactive').length;
  const adminUsers = users.filter(user => user.role === 'admin').length;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedUsers.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-8">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchUsers}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center mx-auto"
        >
          <FaSync className="mr-2" /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Usuarios</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard title="Total Usuarios" value={users.length} icon={<FaUser />} color="from-blue-500 to-blue-700" />
        <StatCard title="Usuarios Activos" value={activeUsers} icon={<FaUserCheck />} color="from-green-500 to-green-700" />
        <StatCard title="Administradores" value={adminUsers} icon={<FaUserCog />} color="from-yellow-500 to-orange-600" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Usuarios</h2>
          <Link
            to="/home/usuarios/add"
            className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-900 transition-colors duration-200 flex items-center"
          >
            <FaPlus className="mr-2" /> Añadir Usuario
          </Link>
        </div>
        <div className="p-4 flex flex-wrap justify-between items-center bg-gray-100">
          <div className="flex items-center mb-2 md:mb-0">
            <FaSearch className="text-blue-700 mr-2" />
            <input
              type="text"
              placeholder="Buscar por Nombre, Usuario o Email"
              className="border p-2 rounded w-80"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-4">
            <div>
              <label htmlFor="roleFilter" className="mr-2">Filtrar por Rol:</label>
              <select
                id="roleFilter"
                className="border p-2 rounded"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="user">Usuario</option>
                <option value="admin">Administrador</option>
                <option value="userManagement">Gestión de Usuarios</option>
              </select>
            </div>
            <div>
              <label htmlFor="itemsPerPage" className="mr-2">Mostrar:</label>
              <select
                id="itemsPerPage"
                className="border p-2 rounded"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={filteredAndSortedUsers.length}>Todos</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('usuario')}>
                  Usuario {sortColumn === 'usuario' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('nombreCompleto')}>
                  Nombre Completo {sortColumn === 'nombreCompleto' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('email')}>
                  Email {sortColumn === 'email' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('role')}>
                  Rol {sortColumn === 'role' && (sortDirection === 'asc' ? <FaSortAmountUp className="inline" /> : <FaSortAmountDown className="inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{user.usuario}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{user.nombreCompleto}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.role === 'admin' ? 'bg-red-100 text-red-800' : 
                      user.role === 'userManagement' ? 'bg-yellow-100 text-yellow-800' : 
                      user.role === 'inactive' ? 'bg-gray-100 text-gray-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <Link to={`/home/usuarios/${user.id}`} className="text-blue-600 hover:text-blue-900">
                        <FaEye className="h-5 w-5" />
                      </Link>
                      <Link to={`/home/usuarios/edit/${user.id}`} className="text-green-600 hover:text-green-900">
                        <FaEdit className="h-5 w-5" />
                      </Link>
                      <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-900">
                        <FaTrash className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 flex justify-between items-center bg-gray-50">
          <div>
            Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredAndSortedUsers.length)} de {filteredAndSortedUsers.length} usuarios
          </div>
          <div>
            {Array.from({ length: Math.ceil(filteredAndSortedUsers.length / itemsPerPage) }, (_, i) => (
              <button
                key={i}
                onClick={() => paginate(i + 1)}
                className={`mx-1 px-3 py-1 rounded ${
                  currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                {i + 1}
              </button>
            ))}
          
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        <div className="bg-white shadow-md rounded-lg overflow-hidden p-4">
          <h3 className="text-xl font-semibold mb-4">Distribución de Roles de Usuario</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={roleData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="role" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Cantidad de Usuarios">
                {roleData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={ROLE_COLORS[entry.role] || '#8884d8'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white shadow-md rounded-lg overflow-hidden p-4">
          <h3 className="text-xl font-semibold mb-4">Estado de Actividad de Usuarios</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={activityData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {activityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={index === 0 ? '#4CAF50' : '#9E9E9E'} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}